import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

// Helper to check if we're in a browser environment
const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Get item from storage (returns string or null, no forced JSON parsing)
export const getItem = async (key: string): Promise<string | null> => {
  if (Platform.OS === 'web') {
    if (isBrowser()) {
      try {
        const localValue = window.localStorage.getItem(key);
        if (localValue !== null) {
          return localValue;
        }
      } catch (e) {
        console.error(`Error reading ${key} from localStorage:`, e);
      }
    }

    try {
      const asyncValue = await AsyncStorage.getItem(key);
      if (asyncValue !== null) {
        if (isBrowser()) {
          try {
            window.localStorage.setItem(key, asyncValue);
          } catch (e) {
            console.error(`Error backfilling ${key} to localStorage:`, e);
          }
        }
        return asyncValue;
      }
    } catch (e) {
      console.error(`Error reading ${key} from AsyncStorage on web:`, e);
    }

    console.warn(`No storage value available yet for key ${key} on web`);
    return null;
  } else {
    try {
      return await AsyncStorage.getItem(key); // Return raw string or null
    } catch (e) {
      console.error(`Error reading ${key} from AsyncStorage:`, e);
      return null;
    }
  }
};

// Set item in storage (accepts string directly)
export const setItem = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === 'web') {
    try {
      await AsyncStorage.setItem(key, value);
    } catch (e) {
      console.error(`Error writing ${key} to AsyncStorage on web:`, e);
    }

    if (isBrowser()) {
      try {
        window.localStorage.setItem(key, value); // Store raw string
      } catch (e) {
        console.error(`Error writing ${key} to localStorage:`, e);
      }
    } else {
      console.warn(`localStorage not available yet for key ${key} on web; skipping localStorage mirror`);
    }
  } else {
    try {
      await AsyncStorage.setItem(key, value); // Store raw string
    } catch (e) {
      console.error(`Error writing ${key} to AsyncStorage:`, e);
    }
  }
};

// Remove item from storage
export const removeItem = async (key: string): Promise<void> => {
  if (Platform.OS === 'web') {
    try {
      await AsyncStorage.removeItem(key);
    } catch (e) {
      console.error(`Error removing ${key} from AsyncStorage on web:`, e);
    }

    if (isBrowser()) {
      try {
        window.localStorage.removeItem(key);
      } catch (e) {
        console.error(`Error removing ${key} from localStorage:`, e);
      }
    }
  } else {
    try {
      await AsyncStorage.removeItem(key);
    } catch (e) {
      console.error(`Error removing ${key} from AsyncStorage:`, e);
    }
  }
};

// Clear all storage
export const clear = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    try {
      await AsyncStorage.clear();
    } catch (e) {
      console.error('Error clearing AsyncStorage on web:', e);
    }

    if (isBrowser()) {
      try {
        window.localStorage.clear();
      } catch (e) {
        console.error('Error clearing localStorage:', e);
      }
    }
  } else {
    try {
      await AsyncStorage.clear();
    } catch (e) {
      console.error('Error clearing AsyncStorage:', e);
    }
  }
};